import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';

import MainLayout from '../../components/layouts/MainLayout';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Switch, 
  FormControlLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CardMedia,
  CardActions,
  Badge,
  Skeleton
} from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import ArticleIcon from '@mui/icons-material/Article';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ToysIcon from '@mui/icons-material/Toys';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { CONTEXTS, CONTROLLERS } from 'figshelf-modules';
import StorageIcon from '@mui/icons-material/Storage';
import AdminSocketService, { ADMIN_SOCKET_EVENTS } from '../../services/AdminSocketService';
import { SERVICES } from 'figshelf-modules';

const ModeratorControls = () => {
  const [autoModeration, setAutoModeration] = React.useState(true);
  const [contentFiltering, setContentFiltering] = React.useState(true);
  const [userReporting, setUserReporting] = React.useState(true);
  const [approvalTab, setApprovalTab] = React.useState(0);

  const apiService = useContext(CONTEXTS.APIContext);
  const auth = useContext(CONTEXTS.AuthContext);
  const companyController = useMemo(() => new CONTROLLERS.CompanyController(apiService), [apiService]);
  const figureController = useMemo(() => new CONTROLLERS.FigureController(apiService), [apiService]);
  const figureDatabaseController = useMemo(() => new CONTROLLERS.FigureDatabaseController(apiService), [apiService]);

  const [companyRequests, setCompanyRequests] = useState([]);
  const [figureRequests, setFigureRequests] = useState([]);
  const [figureDatabaseDrafts, setFigureDatabaseDrafts] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [figureLoading, setFigureLoading] = useState(false);
  const [figureDatabaseLoading, setFigureDatabaseLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [totalItems, setTotalItems] = useState(0);
  const [figurePage, setFigurePage] = useState(1);
  const [figureRowsPerPage, setFigureRowsPerPage] = useState(9); // 9 items for 3x3 grid
  const [totalFigureItems, setTotalFigureItems] = useState(0);
  const [figureDatabasePage, setFigureDatabasePage] = useState(1);
  const [figureDatabaseRowsPerPage, setFigureDatabaseRowsPerPage] = useState(12);
  const [totalFigureDatabaseItems, setTotalFigureDatabaseItems] = useState(0);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedFigure, setSelectedFigure] = useState(null);
  const [selectedFigureDatabaseDraft, setSelectedFigureDatabaseDraft] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewFigureDialogOpen, setViewFigureDialogOpen] = useState(false);
  const [viewFigureDatabaseDialogOpen, setViewFigureDatabaseDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectFigureDialogOpen, setRejectFigureDialogOpen] = useState(false);
  const [rejectFigureDatabaseDialogOpen, setRejectFigureDatabaseDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionType, setRejectionType] = useState('company'); // 'company', 'figure', or 'figureDatabase'
  const [inFlightApprovals, setInFlightApprovals] = useState({});

  const getStatusRank = (status) => {
    const v = String(status || '').toLowerCase();
    if (v === 'pending') return 0;
    if (v === 'approved') return 1;
    if (v === 'rejected') return 2;
    return 3;
  };

  const fetchCompanyRequests = useCallback(async () => {
    try {
      setCompanyLoading(true);

      const cacheParams = { skip: page * rowsPerPage, take: rowsPerPage, status: 'pending' };
      const cacheKey = SERVICES.makeAdminCacheKey({
        userId: auth?.user?.id,
        scope: 'moderator_controls:company_drafts',
        params: cacheParams,
      });

      const cached = await SERVICES.getCachedAdminList(cacheKey, { maxAgeMs: 60 * 1000 });
      if (cached?.list && Array.isArray(cached.list)) {
        setCompanyRequests(cached.list);
        setTotalItems(Number.isFinite(cached.totalItems) ? cached.totalItems : 0);
      }

      const response = await companyController.getPendingCompaniesAsAdmin({skip: page * rowsPerPage, take: rowsPerPage});

      const list = (response.data.data || []).slice().sort((a, b) => {
        const rank = getStatusRank(a?.status) - getStatusRank(b?.status);
        if (rank !== 0) return rank;
        return new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime();
      });

      setCompanyRequests(list);
      setTotalItems(response.data.meta?.totalItems || 0);

      await SERVICES.cacheAdminList(
        cacheKey,
        { list, totalItems: response.data.meta?.totalItems || 0 },
        { scope: 'moderator_controls:company_drafts' },
      );
    } catch (error) {
      console.error('Error fetching company requests:', error);
    } finally {
      setCompanyLoading(false);
    }
  }, [companyController, auth?.user?.id, page, rowsPerPage]);

  const fetchFigureRequests = useCallback(async () => {
    try {
      setFigureLoading(true);

      const cacheParams = { skip: figurePage, take: figureRowsPerPage };
      const cacheKey = SERVICES.makeAdminCacheKey({
        userId: auth?.user?.id,
        scope: 'moderator_controls:figure_drafts',
        params: cacheParams,
      });

      const cached = await SERVICES.getCachedAdminList(cacheKey, { maxAgeMs: 60 * 1000 });
      if (cached?.list && Array.isArray(cached.list)) {
        setFigureRequests(cached.list);
        setTotalFigureItems(Number.isFinite(cached.totalItems) ? cached.totalItems : 0);
      }

      const response = await figureController.getFigureDrafts({ skip: figurePage, take: figureRowsPerPage });
      const list = (response.data.data || []).slice().sort((a, b) => {
        const rank = getStatusRank(a?.status) - getStatusRank(b?.status);
        if (rank !== 0) return rank;
        return new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime();
      });
      setFigureRequests(list);
      setTotalFigureItems(response.data.meta?.total || 0);

      await SERVICES.cacheAdminList(
        cacheKey,
        { list, totalItems: response.data.meta?.total || 0 },
        { scope: 'moderator_controls:figure_drafts' },
      );
    } catch (error) {
      console.error('Error fetching figure requests:', error);
    } finally {
      setFigureLoading(false);
    }
  }, [figureController, auth?.user?.id, figurePage, figureRowsPerPage]);

  const fetchFigureDatabaseDrafts = useCallback(async () => {
    try {
      setFigureDatabaseLoading(true);

      const cacheParams = {
        page: figureDatabasePage,
        limit: figureDatabaseRowsPerPage,
        status: 'pending',
      };

      const cacheKey = SERVICES.makeAdminCacheKey({
        userId: auth?.user?.id,
        scope: 'moderator_controls:figure_database_drafts',
        params: cacheParams,
      });

      const cached = await SERVICES.getCachedAdminList(cacheKey, { maxAgeMs: 60 * 1000 });
      if (cached?.list && Array.isArray(cached.list)) {
        setFigureDatabaseDrafts(cached.list);
        setTotalFigureDatabaseItems(Number.isFinite(cached.totalItems) ? cached.totalItems : 0);
      }

      const response = await figureDatabaseController.getFigureDatabaseDrafts({
        page: figureDatabasePage,
        limit: figureDatabaseRowsPerPage,
        status: 'pending'
      });
      const list = (response.data?.data || []).slice().sort((a, b) => {
        const rank = getStatusRank(a?.status) - getStatusRank(b?.status);
        if (rank !== 0) return rank;
        return new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime();
      });

      setFigureDatabaseDrafts(list);
      setTotalFigureDatabaseItems(response.data?.count || 0);

      await SERVICES.cacheAdminList(
        cacheKey,
        { list, totalItems: response.data?.count || 0 },
        { scope: 'moderator_controls:figure_database_drafts' },
      );
    } catch (error) {
      console.error('Error fetching figure database drafts:', error);
    } finally {
      setFigureDatabaseLoading(false);
    }
  }, [figureDatabaseController, auth?.user?.id, figureDatabasePage, figureDatabaseRowsPerPage]);

  useEffect(() => {
    fetchCompanyRequests();
  }, [fetchCompanyRequests]);

  useEffect(() => {
    fetchFigureRequests();
  }, [fetchFigureRequests]);

  useEffect(() => {
    fetchFigureDatabaseDrafts();
  }, [fetchFigureDatabaseDrafts]);

  useEffect(() => {
    const socketService = AdminSocketService.getInstance();

    const handleCompanyDraft = (payload) => {
      try {
        fetchCompanyRequests();
      } catch (_) {}
    };

    const handleFigureDraft = (payload) => {
      try {
        fetchFigureRequests();
      } catch (_) {}
    };

    const handleFigureDatabaseDraft = (payload) => {
      try {
        fetchFigureDatabaseDrafts();
      } catch (_) {}
    };

    const offCompany = socketService.on(ADMIN_SOCKET_EVENTS.COMPANY_DRAFT_SUBMITTED, handleCompanyDraft);
    const offFigure = socketService.on(ADMIN_SOCKET_EVENTS.FIGURE_DRAFT_SUBMITTED, handleFigureDraft);
    const offFigureDb = socketService.on(
      ADMIN_SOCKET_EVENTS.FIGURE_DATABASE_DRAFT_SUBMITTED,
      handleFigureDatabaseDraft,
    );

    return () => {
      try {
        offCompany?.();
        offFigure?.();
        offFigureDb?.();
      } catch (_) {}
    };
  }, [fetchCompanyRequests, fetchFigureRequests, fetchFigureDatabaseDrafts]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeFigurePage = (event, newPage) => {
    setFigurePage(newPage + 1);
  };

  const handleChangeFigureRowsPerPage = (event) => {
    setFigureRowsPerPage(parseInt(event.target.value, 10));
    setFigurePage(1);
  };

  const handleChangeFigureDatabasePage = (event, newPage) => {
    setFigureDatabasePage(newPage + 1);
  };

  const handleChangeFigureDatabaseRowsPerPage = (event) => {
    setFigureDatabaseRowsPerPage(parseInt(event.target.value, 10));
    setFigureDatabasePage(1);
  };

  const handleViewCompany = (company) => {
    setSelectedCompany(company);
    setViewDialogOpen(true);
  };

  const handleViewFigure = (figure) => {
    setSelectedFigure(figure);
    setViewFigureDialogOpen(true);
  };

  const handleViewFigureDatabaseDraft = (draft) => {
    setSelectedFigureDatabaseDraft(draft);
    setViewFigureDatabaseDialogOpen(true);
  };

  const handleApproveCompany = async (companyId) => {
    const inFlightKey = `company:${companyId}`;
    if (inFlightApprovals[inFlightKey]) return;
    try {
      setInFlightApprovals((prev) => ({ ...prev, [inFlightKey]: true }));
      await companyController.approveCompany(companyId).then((res) => {
      });
      // Reset to first page if current page becomes empty
      if (companyRequests.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        fetchCompanyRequests(); // Refresh the list
      }
    } catch (error) {
      console.error('Error approving company:', error);
    } finally {
      setInFlightApprovals((prev) => {
        const next = { ...prev };
        delete next[inFlightKey];
        return next;
      });
    }
  };

  const handleApproveFigure = async (figureId) => {
    const inFlightKey = `figure:${figureId}`;
    if (inFlightApprovals[inFlightKey]) return;
    try {
      setInFlightApprovals((prev) => ({ ...prev, [inFlightKey]: true }));
      await figureController.approveFigure(figureId);
      // Reset to first page if current page becomes empty
      if (figureRequests.length === 1 && figurePage > 1) {
        setFigurePage(figurePage - 1);
      } else {
        fetchFigureRequests(); // Refresh the list
      }
    } catch (error) {
      console.error('Error approving figure:', error);
    } finally {
      setInFlightApprovals((prev) => {
        const next = { ...prev };
        delete next[inFlightKey];
        return next;
      });
    }
  };

  const handleRejectCompany = async (companyId) => {
    try {
      await companyController.rejectCompany(companyId, { reason: rejectionReason });
      setRejectDialogOpen(false);
      setRejectionReason('');
      // Reset to first page if current page becomes empty
      if (companyRequests.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        fetchCompanyRequests(); // Refresh the list
      }
      console.log('Company rejected:', companyId);
    } catch (error) {
      console.error('Error rejecting company:', error);
    }
  };

  const handleRejectFigure = async (figureId) => {
    try {
      await figureController.rejectFigure(figureId, rejectionReason);
      setRejectFigureDialogOpen(false);
      setRejectionReason('');
      // Reset to first page if current page becomes empty
      if (figureRequests.length === 1 && figurePage > 1) {
        setFigurePage(figurePage - 1);
      } else {
        fetchFigureRequests(); // Refresh the list
      }
    } catch (error) {
      console.error('Error rejecting figure:', error);
    }
  };

  const handleApproveFigureDatabaseDraft = async (draftId) => {
    const inFlightKey = `figureDatabase:${draftId}`;
    if (inFlightApprovals[inFlightKey]) return;
    try {
      setInFlightApprovals((prev) => ({ ...prev, [inFlightKey]: true }));
      await figureDatabaseController.approveFigureDatabaseDraft(draftId);
      // Reset to first page if current page becomes empty
      if (figureDatabaseDrafts.length === 1 && figureDatabasePage > 1) {
        setFigureDatabasePage(figureDatabasePage - 1);
      } else {
        fetchFigureDatabaseDrafts(); // Refresh the list
      }
    } catch (error) {
      console.error('Error approving figure database draft:', error);
    } finally {
      setInFlightApprovals((prev) => {
        const next = { ...prev };
        delete next[inFlightKey];
        return next;
      });
    }
  };

  const handleRejectFigureDatabaseDraft = async (draftId) => {
    try {
      await figureDatabaseController.rejectFigureDatabaseDraft(draftId, rejectionReason);
      setRejectFigureDatabaseDialogOpen(false);
      setRejectionReason('');
      // Reset to first page if current page becomes empty
      if (figureDatabaseDrafts.length === 1 && figureDatabasePage > 1) {
        setFigureDatabasePage(figureDatabasePage - 1);
      } else {
        fetchFigureDatabaseDrafts(); // Refresh the list
      }
    } catch (error) {
      console.error('Error rejecting figure database draft:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
      case 'approved':
        return 'success';
      case 'inactive':
      case 'rejected':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleApprovalTabChange = (event, newValue) => {
    setApprovalTab(newValue);
  };

  const renderFigureRequestsTable = () => {
    if (figureLoading && figureRequests.length === 0) {
      return (
        <Box>
          <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
        </Box>
      );
    }

    if (figureRequests.length === 0) {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100%',
          minHeight: 500,
          color: 'text.secondary'
        }}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <ToysIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No figures pending approval
            </Typography>
            <Typography variant="body2" color="text.secondary">
              When there are figures that need approval, they will appear here.
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box>
        <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Origin</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {figureRequests.map((figure) => {
                const inFlightKey = `figure:${figure.id}`;
                const isInFlight = !!inFlightApprovals[inFlightKey];
                const primaryUrl = (Array.isArray(figure.figureImages) && figure.figureImages[0])
                  ? figure.figureImages[0]
                  : (figure.imageUrl || null);
                return (
                  <TableRow
                    key={figure.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleViewFigure(figure)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                        <Avatar
                          variant="rounded"
                          src={primaryUrl || undefined}
                          sx={{ width: 34, height: 34, borderRadius: 1, bgcolor: 'background.default' }}
                        >
                          <ToysIcon fontSize="small" />
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700} sx={{ maxWidth: 420 }} noWrap>
                            {figure.name || 'Untitled'}
                          </Typography>
                          {(figure.character || figure.classification) ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                              {(figure.character || '—')}{figure.classification ? `  •  ${figure.classification}` : ''}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={figure.status} color={getStatusColor(figure.status)} size="small" />
                    </TableCell>
                    <TableCell>
                      {figure.category ? <Chip label={figure.category} size="small" variant="outlined" /> : '—'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {figure.origin || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {figure.createdBy?.username || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {figure.createdAt ? new Date(figure.createdAt).toLocaleDateString() : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Preview">
                        <IconButton size="small" color="primary" onClick={() => handleViewFigure(figure)}>
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isInFlight ? 'Approving...' : 'Approve'}>
                        <span>
                          <IconButton
                            size="small"
                            color="success"
                            disabled={isInFlight}
                            onClick={() => handleApproveFigure(figure.id)}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedFigure(figure);
                            setRejectionType('figure');
                            setRejectFigureDialogOpen(true);
                          }}
                        >
                          <BlockIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[9, 18, 27]}
          component="div"
          count={totalFigureItems}
          rowsPerPage={figureRowsPerPage}
          page={figurePage - 1}
          onPageChange={handleChangeFigurePage}
          onRowsPerPageChange={handleChangeFigureRowsPerPage}
        />
      </Box>
    );
  };

  const renderCompanyRequestsTable = () => {
    if (companyLoading && companyRequests.length === 0) {
      return (
        <Box>
          <Grid container spacing={2}>
            {Array.from({ length: Math.min(rowsPerPage, 6) }).map((_, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Card sx={{ height: 220, borderRadius: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                      <Skeleton variant="circular" width={46} height={46} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton width="70%" height={24} />
                        <Skeleton width="50%" height={18} />
                      </Box>
                    </Box>
                    <Skeleton width="40%" height={22} />
                    <Skeleton width="90%" height={18} />
                    <Skeleton width="80%" height={18} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      );
    }

    if (companyRequests.length === 0) {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100%',
          minHeight: 500,
          color: 'text.secondary'
        }}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <BusinessIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No companies pending approval
            </Typography>
            <Typography variant="body2" color="text.secondary">
              When there are companies that need approval, they will appear here.
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box>
        <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companyRequests.map((company) => {
                const inFlightKey = `company:${company.id}`;
                const isInFlight = !!inFlightApprovals[inFlightKey];
                return (
                  <TableRow
                    key={company.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleViewCompany(company)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={800} sx={{ maxWidth: 420 }} noWrap>
                        {company.name || 'Untitled'}
                      </Typography>
                      {company.description ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                          {company.description}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Chip label={company.status} color={getStatusColor(company.status)} size="small" />
                    </TableCell>
                    <TableCell>
                      {company.type ? <Chip label={company.type} size="small" variant="outlined" /> : '—'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {company.email || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {company.createdBy?.username || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {company.createdAt ? new Date(company.createdAt).toLocaleDateString() : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Preview">
                        <IconButton size="small" color="primary" onClick={() => handleViewCompany(company)}>
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isInFlight ? 'Approving...' : 'Approve'}>
                        <span>
                          <IconButton
                            size="small"
                            color="success"
                            disabled={isInFlight}
                            onClick={() => handleApproveCompany(company.id)}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedCompany(company);
                            setRejectionType('company');
                            setRejectDialogOpen(true);
                          }}
                        >
                          <BlockIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[6, 12, 24]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    );
  };

  const renderFigureDatabaseDraftsTable = () => {
    if (figureDatabaseLoading && figureDatabaseDrafts.length === 0) {
      return (
        <Box>
          <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
        </Box>
      );
    }

    if (figureDatabaseDrafts.length === 0) {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100%',
          minHeight: 500,
          color: 'text.secondary'
        }}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <StorageIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No figure database drafts pending approval
            </Typography>
            <Typography variant="body2" color="text.secondary">
              When there are figure database submissions that need approval, they will appear here.
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box>
        <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submission</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {figureDatabaseDrafts.map((draft) => {
                const inFlightKey = `figureDatabase:${draft.id}`;
                const isInFlight = !!inFlightApprovals[inFlightKey];
                const isApproved = String(draft.status || '').toLowerCase() === 'approved';
                const figureImageObjects = Array.isArray(draft.figure?.figureImages) ? draft.figure.figureImages : [];
                const primaryUrl = figureImageObjects?.[0]?.url || null;
                const shortTarget = draft.figureId ? String(draft.figureId).slice(0, 8) : null;
                return (
                  <TableRow
                    key={draft.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleViewFigureDatabaseDraft(draft)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                        <Avatar
                          variant="rounded"
                          src={primaryUrl || undefined}
                          sx={{ width: 34, height: 34, borderRadius: 1, bgcolor: 'background.default' }}
                        >
                          <StorageIcon fontSize="small" />
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={800} sx={{ maxWidth: 420 }} noWrap>
                            {draft.figure?.name || 'Figure Database Entry'}
                          </Typography>
                          {draft.description ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                              {draft.description}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={draft.status} color={getStatusColor(String(draft.status || '').toLowerCase())} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={draft.isCompanySubmission ? 'Company' : 'Individual'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {draft.companyName || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {draft.createdBy?.username || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {draft.createdAt ? new Date(draft.createdAt).toLocaleDateString() : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Preview">
                        <IconButton size="small" color="primary" onClick={() => handleViewFigureDatabaseDraft(draft)}>
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      {!isApproved ? (
                        <>
                          <Tooltip title={isInFlight ? 'Approving...' : 'Approve'}>
                            <span>
                              <IconButton
                                size="small"
                                color="success"
                                disabled={isInFlight}
                                onClick={() => handleApproveFigureDatabaseDraft(draft.id)}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setSelectedFigureDatabaseDraft(draft);
                                setRejectionType('figureDatabase');
                                setRejectFigureDatabaseDialogOpen(true);
                              }}
                            >
                              <BlockIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[6, 12, 24]}
          component="div"
          count={totalFigureDatabaseItems}
          rowsPerPage={figureDatabaseRowsPerPage}
          page={figureDatabasePage - 1}
          onPageChange={handleChangeFigureDatabasePage}
          onRowsPerPageChange={handleChangeFigureDatabaseRowsPerPage}
        />
      </Box>
    );
  };

  const renderApprovalContent = () => {
    const tabNames = ['company','figures', 'figureDatabase'];
    const currentTab = tabNames[approvalTab];

    if (currentTab === 'company') {
      return renderCompanyRequestsTable();
    }

    if (currentTab === 'figures') {
      return renderFigureRequestsTable();
    }

    if (currentTab === 'figureDatabase') {
      return renderFigureDatabaseDraftsTable();
    }

    return null;
  };

  const moderationRules = [
    {
      id: 1,
      name: 'Spam Detection',
      description: 'Automatically detect and flag spam content',
      status: 'active',
      priority: 'high'
    },
    {
      id: 2,
      name: 'Inappropriate Content',
      description: 'Filter out inappropriate or offensive content',
      status: 'active',
      priority: 'high'
    },
    {
      id: 3,
      name: 'Duplicate Content',
      description: 'Identify and flag duplicate posts',
      status: 'inactive',
      priority: 'medium'
    },
    {
      id: 4,
      name: 'User Behavior',
      description: 'Monitor user behavior patterns',
      status: 'active',
      priority: 'medium'
    }
  ];



  return (
    <MainLayout>
      <Box>
        

        {/* Approvals Section */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={3}>
              Approvals
            </Typography>
            
            {/* Topbar with Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={approvalTab}
                onChange={handleApprovalTabChange}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab
                  icon={<BusinessIcon />}
                  iconPosition="start"
                  label={
                    <Badge color="primary" badgeContent={totalItems} max={999}>
                      <span>Company</span>
                    </Badge>
                  }
                />
                <Tab
                  icon={<ToysIcon />}
                  iconPosition="start"
                  label={
                    <Badge color="primary" badgeContent={totalFigureItems} max={999}>
                      <span>Figures</span>
                    </Badge>
                  }
                />
                <Tab
                  icon={<StorageIcon />}
                  iconPosition="start"
                  label={
                    <Badge color="primary" badgeContent={totalFigureDatabaseItems} max={999}>
                      <span>Figure Database</span>
                    </Badge>
                  }
                />
              </Tabs>
            </Box>

            {/* Content Area with Fixed Height */}
            <Box sx={{ minHeight: 600, display: 'flex', flexDirection: 'column' }}>
              {renderApprovalContent()}
            </Box>
          </CardContent>
        </Card>

        {/* View Company Dialog */}
        <Dialog 
          open={viewDialogOpen} 
          onClose={() => setViewDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Company Details</DialogTitle>
          <DialogContent>
            {selectedCompany && (
              <Grid container spacing={3}>
                {/* Company Logo/Image */}
                {(selectedCompany.currentAvatar || selectedCompany.logoUrl) && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <img 
                        src={selectedCompany.currentAvatar || selectedCompany.logoUrl} 
                        alt={selectedCompany.name}
                        style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
                      />
                    </Box>
                  </Grid>
                )}
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Company Name</Typography>
                  <Typography variant="body1" fontWeight={600}>{selectedCompany.name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Email</Typography>
                  <Typography variant="body1">{selectedCompany.email}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Type</Typography>
                  <Chip label={selectedCompany.type} color="primary" size="small" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Status</Typography>
                  <Chip label={selectedCompany.status} color={getStatusColor(selectedCompany.status)} size="small" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Phone Number</Typography>
                  <Typography variant="body1">{selectedCompany.phoneNumber || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Website</Typography>
                  <Typography variant="body1">{selectedCompany.website || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Description</Typography>
                  <Typography variant="body1">{selectedCompany.description || 'No description provided'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Address</Typography>
                  <Typography variant="body1">{selectedCompany.address || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Created By</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      <PersonIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedCompany.createdBy?.username || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedCompany.createdBy?.email || 'No email'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Created Date</Typography>
                  <Typography variant="body1">
                    {new Date(selectedCompany.createdAt).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
            <Button 
              onClick={() => handleApproveCompany(selectedCompany?.id)}
              color="success"
              variant="contained"
              startIcon={<CheckCircleIcon />}
            >
              Approve
            </Button>
            <Button 
              onClick={() => {
                setViewDialogOpen(false);
                setRejectionType('company');
                setRejectDialogOpen(true);
              }}
              color="error"
              variant="outlined"
              startIcon={<BlockIcon />}
            >
              Reject
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Figure Dialog */}
        <Dialog 
          open={viewFigureDialogOpen} 
          onClose={() => setViewFigureDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Figure Details</DialogTitle>
          <DialogContent>
            {selectedFigure && (
              <Grid container spacing={3}>
                {/* Figure Image */}
                {(selectedFigure.imageUrl || (selectedFigure.figureImages && selectedFigure.figureImages.length)) && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <CardMedia
                        component="img"
                        image={(selectedFigure.figureImages && selectedFigure.figureImages[0]) || selectedFigure.imageUrl}
                        alt={selectedFigure.name}
                        sx={{ 
                          maxWidth: '100%', 
                          maxHeight: '400px', 
                          objectFit: 'contain',
                          borderRadius: 2,
                          boxShadow: 2
                        }}
                      />
                    </Box>

                    {Array.isArray(selectedFigure.figureImages) && selectedFigure.figureImages.length > 1 && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mb: 2 }}>
                        {selectedFigure.figureImages.map((url, idx) => (
                          <Box
                            key={idx}
                            component="img"
                            src={url}
                            alt={`${selectedFigure.name} ${idx + 1}`}
                            sx={{
                              width: 72,
                              height: 72,
                              borderRadius: 1,
                              objectFit: 'cover',
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>{selectedFigure.name}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Chip label={selectedFigure.status} color={getStatusColor(selectedFigure.status)} size="small" />
                    {selectedFigure.category && <Chip label={selectedFigure.category} variant="outlined" size="small" />}
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Classification</Typography>
                  <Typography variant="body1">{selectedFigure.classification || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Character</Typography>
                  <Typography variant="body1">{selectedFigure.character || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Origin</Typography>
                  <Typography variant="body1">{selectedFigure.origin || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Material</Typography>
                  <Typography variant="body1">{selectedFigure.material || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Scale</Typography>
                  <Typography variant="body1">{selectedFigure.scale || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Version</Typography>
                  <Typography variant="body1">{selectedFigure.version || 'Not specified'}</Typography>
                </Grid>
                {selectedFigure.artists && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Artists</Typography>
                    <Typography variant="body1">{selectedFigure.artists}</Typography>
                  </Grid>
                )}
                {selectedFigure.price && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Price</Typography>
                    <Typography variant="body1">
                      {selectedFigure.priceCurrency || '$'} {selectedFigure.price}
                    </Typography>
                  </Grid>
                )}
                {selectedFigure.releaseDate && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Release Date</Typography>
                    <Typography variant="body1">
                      {new Date(selectedFigure.releaseDate).toLocaleDateString()}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Description</Typography>
                  <Typography variant="body1">{selectedFigure.description || 'No description provided'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Created By</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      <PersonIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedFigure.createdBy?.username || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedFigure.createdBy?.email || 'No email'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Created Date</Typography>
                  <Typography variant="body1">
                    {new Date(selectedFigure.createdAt).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewFigureDialogOpen(false)}>Close</Button>
            <Button 
              onClick={() => handleApproveFigure(selectedFigure?.id)}
              color="success"
              variant="contained"
              startIcon={<CheckCircleIcon />}
            >
              Approve
            </Button>
            <Button 
              onClick={() => {
                setViewFigureDialogOpen(false);
                setRejectionType('figure');
                setRejectFigureDialogOpen(true);
              }}
              color="error"
              variant="outlined"
              startIcon={<BlockIcon />}
            >
              Reject
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reject Company Dialog */}
        <Dialog 
          open={rejectDialogOpen} 
          onClose={() => setRejectDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reject Company</DialogTitle>
          <DialogContent>
            <Typography variant="body2" gutterBottom>
              Are you sure you want to reject "{selectedCompany?.name}"?
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Rejection Reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              margin="normal"
              placeholder="Please provide a reason for rejection..."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={() => handleRejectCompany(selectedCompany?.id)}
              color="error"
              variant="contained"
            >
              Reject
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reject Figure Dialog */}
        <Dialog 
          open={rejectFigureDialogOpen} 
          onClose={() => setRejectFigureDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reject Figure</DialogTitle>
          <DialogContent>
            <Typography variant="body2" gutterBottom>
              Are you sure you want to reject "{selectedFigure?.name}"?
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Rejection Reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              margin="normal"
              placeholder="Please provide a reason for rejection..."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectFigureDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={() => handleRejectFigure(selectedFigure?.id)}
              color="error"
              variant="contained"
            >
              Reject
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Figure Database Draft Dialog */}
        <Dialog 
          open={viewFigureDatabaseDialogOpen} 
          onClose={() => setViewFigureDatabaseDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Figure Database Draft Details</DialogTitle>
          <DialogContent>
            {selectedFigureDatabaseDraft && (
              <Grid container spacing={2}>
                {Array.isArray(selectedFigureDatabaseDraft.figure?.figureImages) && selectedFigureDatabaseDraft.figure.figureImages.length > 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <CardMedia
                        component="img"
                        image={selectedFigureDatabaseDraft.figure.figureImages[0]?.url}
                        alt={selectedFigureDatabaseDraft.name}
                        sx={{
                          maxWidth: '100%',
                          maxHeight: '400px',
                          objectFit: 'contain',
                          borderRadius: 2,
                          boxShadow: 2,
                        }}
                      />
                    </Box>

                    {selectedFigureDatabaseDraft.figure.figureImages.length > 1 && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mb: 1 }}>
                        {selectedFigureDatabaseDraft.figure.figureImages
                          .map((img) => img?.url)
                          .filter(Boolean)
                          .map((url, idx) => (
                            <Box
                              key={idx}
                              component="img"
                              src={url}
                              alt={`${selectedFigureDatabaseDraft.name} ${idx + 1}`}
                              sx={{
                                width: 72,
                                height: 72,
                                borderRadius: 1,
                                objectFit: 'cover',
                                border: '1px solid',
                                borderColor: 'divider',
                              }}
                            />
                          ))}
                      </Box>
                    )}
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Figure Name</Typography>
                  <Typography variant="body1" gutterBottom>{selectedFigureDatabaseDraft.name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip
                    label={selectedFigureDatabaseDraft.status}
                    color={getStatusColor(selectedFigureDatabaseDraft.status.toLowerCase())}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Action</Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedFigureDatabaseDraft.figureId ? 'Update existing figure' : 'Create new figure'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Target</Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedFigureDatabaseDraft.figureId ? `Update: ${selectedFigureDatabaseDraft.figureId}` : 'Create new'}
                  </Typography>
                </Grid>

                {selectedFigureDatabaseDraft.figureId ? (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Target Figure (Current)</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 0.5 }}>
                      <Avatar
                        variant="rounded"
                        src={selectedFigureDatabaseDraft.figure?.figureImages?.[0]?.url || undefined}
                        sx={{ width: 40, height: 40, borderRadius: 1, bgcolor: 'background.default' }}
                      >
                        <ToysIcon fontSize="small" />
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                          {selectedFigureDatabaseDraft.figure?.name || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {selectedFigureDatabaseDraft.figureId}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ) : null}

                {selectedFigureDatabaseDraft.figure ? (
                  <>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Figure Information (Submitted/Result)</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Company</Typography>
                      <Typography variant="body1">{selectedFigureDatabaseDraft.figure.company || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Category</Typography>
                      <Typography variant="body1">{selectedFigureDatabaseDraft.figure.category || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Classification</Typography>
                      <Typography variant="body1">{selectedFigureDatabaseDraft.figure.classification || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Origin</Typography>
                      <Typography variant="body1">{selectedFigureDatabaseDraft.figure.origin || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Character</Typography>
                      <Typography variant="body1">{selectedFigureDatabaseDraft.figure.character || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Material</Typography>
                      <Typography variant="body1">{selectedFigureDatabaseDraft.figure.material || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Scale</Typography>
                      <Typography variant="body1">{selectedFigureDatabaseDraft.figure.scale || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Version</Typography>
                      <Typography variant="body1">{selectedFigureDatabaseDraft.figure.version || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Release Date</Typography>
                      <Typography variant="body1">
                        {selectedFigureDatabaseDraft.figure.releaseDate ? new Date(selectedFigureDatabaseDraft.figure.releaseDate).toLocaleDateString() : '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Price</Typography>
                      <Typography variant="body1">
                        {selectedFigureDatabaseDraft.figure.price ? `${selectedFigureDatabaseDraft.figure.priceCurrency || '$'} ${selectedFigureDatabaseDraft.figure.price}` : '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Description</Typography>
                      <Typography variant="body1">{selectedFigureDatabaseDraft.figure.description || '—'}</Typography>
                    </Grid>
                  </>
                ) : null}
                {selectedFigureDatabaseDraft.metadata && typeof selectedFigureDatabaseDraft.metadata === 'object' && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Metadata</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      {Object.entries(selectedFigureDatabaseDraft.metadata).map(([k, v]) => (
                        <Chip
                          key={k}
                          label={`${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`}
                          size="small"
                          variant="outlined"
                          sx={{
                            maxWidth: '100%',
                            '& .MuiChip-label': {
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedFigureDatabaseDraft.description || 'No description provided'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Submission Type</Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedFigureDatabaseDraft.isCompanySubmission ? 'Company Submission' : 'Individual Submission'}
                  </Typography>
                </Grid>
                {selectedFigureDatabaseDraft.isCompanySubmission && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Company</Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedFigureDatabaseDraft.companyName || 'Unknown Company'}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Created By</Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedFigureDatabaseDraft.createdBy?.username} ({selectedFigureDatabaseDraft.createdBy?.email})
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Created Date</Typography>
                  <Typography variant="body1" gutterBottom>
                    {new Date(selectedFigureDatabaseDraft.createdAt).toLocaleString()}
                  </Typography>
                </Grid>
                {selectedFigureDatabaseDraft.rejectionReason && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Rejection Reason</Typography>
                    <Typography variant="body1" gutterBottom color="error">
                      {selectedFigureDatabaseDraft.rejectionReason}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewFigureDatabaseDialogOpen(false)}>Close</Button>
            {String(selectedFigureDatabaseDraft?.status || '').toLowerCase() !== 'approved' ? (
              <>
                <Button
                  onClick={() => handleApproveFigureDatabaseDraft(selectedFigureDatabaseDraft?.id)}
                  color="success"
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  disabled={!!inFlightApprovals[`figureDatabase:${selectedFigureDatabaseDraft?.id}`]}
                >
                  Approve
                </Button>
                <Button
                  onClick={() => {
                    setViewFigureDatabaseDialogOpen(false);
                    setRejectionType('figureDatabase');
                    setRejectFigureDatabaseDialogOpen(true);
                  }}
                  color="error"
                  variant="outlined"
                  startIcon={<BlockIcon />}
                >
                  Reject
                </Button>
              </>
            ) : null}
          </DialogActions>
        </Dialog>

        {/* Reject Figure Database Draft Dialog */}
        <Dialog 
          open={rejectFigureDatabaseDialogOpen} 
          onClose={() => setRejectFigureDatabaseDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reject Figure Database Draft</DialogTitle>
          <DialogContent>
            <Typography variant="body2" gutterBottom>
              Are you sure you want to reject "{selectedFigureDatabaseDraft?.name}"?
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Rejection Reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              margin="normal"
              placeholder="Please provide a reason for rejection..."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectFigureDatabaseDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={() => handleRejectFigureDatabaseDraft(selectedFigureDatabaseDraft?.id)}
              color="error"
              variant="contained"
            >
              Reject
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};

export default ModeratorControls; 